import type { EnvironmentId, ProjectCheckoutDeletionPreview } from "@cmmd-forge/contracts";

import { readEnvironmentConnection } from "./environments/runtime/service";
import { classifyEnvironmentId } from "./environments/environmentIdClassification";
import type { ProjectGroupRemovalTarget } from "./projectGroupRemovalRunner";
import {
  createRemovableForgeProject,
  previewSidebarProjectCheckoutDeletion,
  removeSidebarProject,
  type RemovableForgeProject,
} from "./components/sidebar/sidebarProjectRemoval";

function unavailablePreview(
  target: ProjectGroupRemovalTarget,
  blocker: string,
): ProjectCheckoutDeletionPreview {
  return {
    projectId: target.projectId,
    canonicalPath: target.path,
    method: "unavailable",
    dirty: false,
    blockers: [blocker],
    fingerprint: null,
  };
}

/**
 * File deletion is owned by the machine holding the checkout. Cloud preview
 * uses CMMD's registered-project authority and never trusts the displayed path.
 */
export async function previewProjectCheckoutDeletion(
  target: ProjectGroupRemovalTarget,
  cloudProject?: RemovableForgeProject,
): Promise<ProjectCheckoutDeletionPreview> {
  if (cloudProject || classifyEnvironmentId(target.environmentId).isCloud) {
    return previewSidebarProjectCheckoutDeletion(
      cloudProject ?? createRemovableForgeProject([target]),
    );
  }

  const connection = readEnvironmentConnection(target.environmentId as EnvironmentId);
  if (!connection) {
    return unavailablePreview(target, "The checkout owner is unreachable.");
  }
  return connection.client.projectCheckoutDeletion.preview({ projectId: target.projectId });
}

export async function previewProjectCheckoutDeletionTargets(
  targets: readonly ProjectGroupRemovalTarget[],
  resolveCloudProject?: (target: ProjectGroupRemovalTarget) => RemovableForgeProject | undefined,
): Promise<ReadonlyMap<string, ProjectCheckoutDeletionPreview>> {
  const settled = await Promise.all(
    targets.map(async (target) => {
      try {
        return {
          target,
          preview: await previewProjectCheckoutDeletion(target, resolveCloudProject?.(target)),
        };
      } catch (error) {
        return {
          target,
          preview: unavailablePreview(
            target,
            error instanceof Error ? error.message : "Checkout preflight failed.",
          ),
        };
      }
    }),
  );
  const previews = new Map<string, ProjectCheckoutDeletionPreview>();
  for (const entry of settled) {
    previews.set(`${entry.target.environmentId}:${entry.target.projectId}`, entry.preview);
  }
  return previews;
}

export async function executeProjectCheckoutDeletion(input: {
  readonly target: ProjectGroupRemovalTarget;
  readonly projectId: string;
  readonly expectedFingerprint: string;
  readonly forceDirty: boolean;
  readonly cloudProject?: RemovableForgeProject;
}) {
  if (input.cloudProject || classifyEnvironmentId(input.target.environmentId).isCloud) {
    await removeSidebarProject(input.cloudProject ?? createRemovableForgeProject([input.target]), {
      destroyRuntime: false,
      checkoutDeletion: {
        previewToken: input.expectedFingerprint,
        forceDirty: input.forceDirty,
      },
    });
    return {
      projectId: input.projectId,
      status: "deleted" as const,
      method: "permanent" as const,
      canonicalPath: input.target.path,
      blockers: [],
    };
  }
  const connection = readEnvironmentConnection(input.target.environmentId as EnvironmentId);
  if (!connection) throw new Error("The checkout owner is unreachable.");
  return connection.client.projectCheckoutDeletion.execute({
    projectId: input.projectId,
    expectedFingerprint: input.expectedFingerprint,
    forceDirty: input.forceDirty,
  });
}
